import React, { useState, useRef, useEffect, useCallback } from 'react';
import marshallImage from '../../assets/images/mg-face-crop.jpeg';

// Types
type GroundingFile = {
  id: string;
  name: string;
  content: string;
};

type SessionUpdateCommand = {
  type: "session.update";
  session: {
    type: "realtime";
    instructions?: string;
    metadata?: Record<string, string>;
    audio?: {
      input?: {
        turn_detection?: {
          type: "server_vad" | "none";
        };
        transcription?: {
          model: "whisper-1";
        };
      };
    };
  };
};

type ConversationItemCreateCommand = {
  type: "conversation.item.create";
  item: {
    type: "message";
    role: "system" | "user" | "assistant";
    content: {
      type: "input_text";
      text: string;
    }[];
  };
};

type ConversationContentItem = {
  type: string;
  text?: string;
  transcript?: string;
};

type ConversationItemEvent = {
  id: string;
  type: string;
  role: "system" | "user" | "assistant";
  content?: ConversationContentItem[];
};

type ConversationItemCreateEvent = {
  type: "conversation.item.create";
  item: ConversationItemEvent;
};

type ResponseCreateCommand = {
  type: "response.create";
  response: {
    conversation?: string | { id: string };
    instructions?: string;
    output_modalities?: ("text" | "audio")[];
  };
};

type InputAudioBufferAppendCommand = {
  type: "input_audio_buffer.append";
  audio: string;
};

type InputAudioBufferClearCommand = {
  type: "input_audio_buffer.clear";
};

type Message = {
  type: string;
};

type ResponseAudioDelta = {
  type: "response.audio.delta" | "response.output_audio.delta";
  delta: string;
};

type ResponseAudioTranscriptDelta = {
  type: "response.audio_transcript.delta" | "response.output_audio_transcript.delta";
  delta: string;
};

type ResponseInputAudioTranscriptionCompleted = {
  type:
    | "response.input_audio_transcription.completed"
    | "conversation.item.input_audio_transcription.completed"
    | "conversation.item.audio_transcription.completed"
    | "input_audio_transcription.completed"
    | "response.input_audio_transcript.completed"
    | "conversation.item.input_audio_transcript.completed"
    | "input_audio_transcript.completed";
  event_id: string;
  item_id: string;
  content_index: number;
  transcript: string;
};

type ResponseInputAudioTranscriptionDelta = {
  type:
    | "response.input_audio_transcription.delta"
    | "conversation.item.input_audio_transcription.delta"
    | "conversation.item.audio_transcription.delta"
    | "input_audio_transcription.delta"
    | "response.input_audio_transcript.delta"
    | "conversation.item.input_audio_transcript.delta"
    | "input_audio_transcript.delta";
  event_id: string;
  item_id: string;
  content_index: number;
  transcript?: string;
  delta?: string;
};

type ResponseDone = {
  type: "response.done";
  event_id: string;
  response: {
    id: string;
    output: { id: string; content?: { transcript: string; type: string }[] }[];
  };
};

type ExtensionMiddleTierToolResponse = {
  type: "extension.middle_tier_tool.response";
  previous_item_id: string;
  tool_name: string;
  tool_result: string;
};

type ToolResult = {
  sources: { chunk_id: string; title: string; chunk: string }[];
};

type ChatSessionMessage = {
  type: "chat_session";
  session_id: string;
};

type ChatMessage = {
  sender: "user" | "ai";
  text: string;
};

type StoreChatMessagePayload = {
  sender: "user" | "assistant";
  message: string;
  refineQuestion?: string;
};

type UseRealTimeParameters = {
  token?: string | null;
  chatSessionId?: string | null;
  userId?: string | null;
  userEmail?: string | null;
  enableInputAudioTranscription?: boolean;
  onWebSocketOpen?: () => void;
  onWebSocketClose?: () => void;
  onWebSocketError?: (event: Event) => void;
  onWebSocketMessage?: (event: MessageEvent<any>) => void;
  onReceivedChatSession?: (message: ChatSessionMessage) => void;
  onReceivedConversationItemCreate?: (message: ConversationItemCreateEvent) => void;
  onReceivedResponseAudioDelta?: (message: ResponseAudioDelta) => void;
  onReceivedInputAudioBufferSpeechStarted?: (message: Message) => void;
  onReceivedResponseDone?: (message: ResponseDone) => void;
  onReceivedExtensionMiddleTierToolResponse?: (message: ExtensionMiddleTierToolResponse) => void;
  onReceivedResponseAudioTranscriptDelta?: (message: ResponseAudioTranscriptDelta) => void;
  onReceivedInputAudioTranscriptionCompleted?: (message: ResponseInputAudioTranscriptionCompleted) => void;
  onReceivedInputAudioTranscriptionDelta?: (message: ResponseInputAudioTranscriptionDelta) => void;
  onReceivedError?: (message: Message) => void;
};

type UseAudioRecorderParameters = {
  onAudioRecorded: (base64: string) => void;
};

type RealtimeWebSocketOptions = {
  onOpen?: () => void;
  onClose?: (event: CloseEvent) => void;
  onError?: (event: Event) => void;
  onMessage?: (event: MessageEvent<any>) => void;
  shouldReconnect?: () => boolean;
};

// Constants
const SAMPLE_RATE = 24000;
const BUFFER_SIZE = 4800;

// Player Class
class Player {
  private playbackNode: AudioWorkletNode | null = null;
  private audioContext: AudioContext | null = null;

  async init(sampleRate: number) {
    this.audioContext = new AudioContext({ sampleRate });
    if (this.audioContext.state === "suspended") {
      await this.audioContext.resume();
    }
    const workletCode = `
      class AudioPlaybackWorklet extends AudioWorkletProcessor {
        constructor() {
          super();
          this.port.onmessage = this.handleMessage.bind(this);
          this.buffer = [];
        }

        handleMessage(event) {
          if (event.data === null) {
            this.buffer = [];
            return;
          }
          this.buffer.push(...event.data);
        }

        process(inputs, outputs, parameters) {
          const output = outputs[0];
          const channel = output[0];
          const available = this.buffer.length;
          const required = channel.length;
          const toProcessCount = Math.min(available, required);

          if (toProcessCount > 0) {
            const toProcess = this.buffer.splice(0, toProcessCount);
            const float32Array = new Float32Array(toProcess.length);
            for (let i = 0; i < toProcess.length; i++) {
              float32Array[i] = toProcess[i];
            }
            channel.set(float32Array);
          }

          return true;
        }
      }

      registerProcessor('audioPlaybackWorklet', AudioPlaybackWorklet);
    `;

    const blob = new Blob([workletCode], { type: 'application/javascript' });
    const url = URL.createObjectURL(blob);
    const audioWorklet = this.audioContext.audioWorklet;
    await audioWorklet.addModule(url);
    this.playbackNode = new AudioWorkletNode(this.audioContext, 'audioPlaybackWorklet');
    this.playbackNode.connect(this.audioContext.destination);
  }

  play(pcmData: Int16Array) {
    if (!this.playbackNode) return;
    const float32Array = new Float32Array(pcmData.length);
    for (let i = 0; i < pcmData.length; i++) {
      float32Array[i] = pcmData[i] / 32768.0;
    }
    this.playbackNode.port.postMessage(Array.from(float32Array));
  }

  clear() {
    if (!this.playbackNode) return;
    this.playbackNode.port.postMessage(null);
  }
}

// Recorder Class
export class Recorder {
  private audioContext?: AudioContext;
  private audioWorkletNode?: AudioWorkletNode;
  private mediaStreamAudioSourceNode?: MediaStreamAudioSourceNode;
  private onAudioData: (data: Float32Array) => void;

  constructor(onAudioData: (data: Float32Array) => void) {
    this.onAudioData = onAudioData;
  }

  async start(stream: MediaStream) {
    this.audioContext = new AudioContext({ sampleRate: SAMPLE_RATE });
    this.mediaStreamAudioSourceNode = this.audioContext.createMediaStreamSource(stream);

    const workletCode = `
      class AudioRecordingWorklet extends AudioWorkletProcessor {
        constructor() {
          super();
          this.port.onmessage = this.handleMessage.bind(this);
        }

        handleMessage(event) {
        }

        process(inputs, outputs, parameters) {
          const input = inputs[0];
          if (input.length > 0) {
            const channelData = input[0];
            this.port.postMessage(channelData);
          }
          return true;
        }
      }

      registerProcessor('audioRecordingWorklet', AudioRecordingWorklet);
    `;

    const blob = new Blob([workletCode], { type: 'application/javascript' });
    const url = URL.createObjectURL(blob);
    const audioWorklet = this.audioContext.audioWorklet;
    await audioWorklet.addModule(url);

    this.audioWorkletNode = new AudioWorkletNode(this.audioContext, 'audioRecordingWorklet');
    this.audioWorkletNode.port.onmessage = (event: MessageEvent) => {
      this.onAudioData(event.data);
    };

    this.mediaStreamAudioSourceNode.connect(this.audioWorkletNode);
    this.audioWorkletNode.connect(this.audioContext.destination);
  }

  async stop() {
    if (this.mediaStreamAudioSourceNode) {
      this.mediaStreamAudioSourceNode.disconnect();
    }
    if (this.audioWorkletNode) {
      this.audioWorkletNode.disconnect();
    }
    if (this.audioContext && this.audioContext.state !== "closed") {
      await this.audioContext.close();
    }
  }
}

// Hooks
function useAudioRecorder({ onAudioRecorded }: UseAudioRecorderParameters) {
  const audioRecorder = useRef<Recorder>();
  let buffer = new Uint8Array();

  const appendToBuffer = (newData: Uint8Array) => {
    const newBuffer = new Uint8Array(buffer.length + newData.length);
    newBuffer.set(buffer);
    newBuffer.set(newData, buffer.length);
    buffer = newBuffer;
  };

  const handleAudioData = (data: Float32Array) => {
    const pcmData = new Int16Array(data.length);
    for (let i = 0; i < data.length; i++) {
      const s = Math.max(-1, Math.min(1, data[i]));
      pcmData[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }

    const uint8Array = new Uint8Array(pcmData.buffer);
    appendToBuffer(uint8Array);

    if (buffer.length >= BUFFER_SIZE) {
      const toSend = new Uint8Array(buffer.slice(0, BUFFER_SIZE));
      buffer = new Uint8Array(buffer.slice(BUFFER_SIZE));

      let binary = "";
      for (let i = 0; i < toSend.length; i++) {
        binary += String.fromCharCode(toSend[i]);
      }
      const base64 = btoa(binary);

      onAudioRecorded(base64);
    }
  };

  const start = async () => {
    if (!audioRecorder.current) {
      audioRecorder.current = new Recorder(handleAudioData);
    }
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        noiseSuppression: true,
        echoCancellation: true,
        autoGainControl: true
      }
    });
    await audioRecorder.current.start(stream);
  };

  const stop = async () => {
    await audioRecorder.current?.stop();
  };

  return { start, stop };
}

function useRealtimeWebSocket(url: string, options: RealtimeWebSocketOptions) {
  const socketRef = useRef<WebSocket | null>(null);
  const optionsRef = useRef(options);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const messageQueueRef = useRef<string[]>([]);

  optionsRef.current = options;

  useEffect(() => {
    if (!url) {
      messageQueueRef.current = [];
      socketRef.current?.close();
      socketRef.current = null;
      return;
    }

    let closedByEffect = false;

    const connect = () => {
      try {
        const socket = new WebSocket(url);
        socketRef.current = socket;

        socket.onopen = () => {
          optionsRef.current.onOpen?.();
          const queuedMessages = messageQueueRef.current.splice(0);
          queuedMessages.forEach(message => socket.send(message));
        };
        socket.onclose = event => {
          optionsRef.current.onClose?.(event as CloseEvent);
          if (!closedByEffect && (optionsRef.current.shouldReconnect?.() ?? true)) {
            reconnectTimeoutRef.current = setTimeout(connect, 1000);
          }
        };
        socket.onerror = event => optionsRef.current.onError?.(event);
        socket.onmessage = event => optionsRef.current.onMessage?.(event);
      } catch (error) {
        console.error("Failed to open realtime WebSocket:", error);
      }
    };

    connect();

    return () => {
      closedByEffect = true;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [url]);

  const sendJsonMessage = useCallback((message: unknown) => {
    if (!url) {
      console.warn("Realtime WebSocket is not ready. Message was not sent.");
      return;
    }

    const payload = JSON.stringify(message);
    const socket = socketRef.current;

    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(payload);
      return;
    }

    messageQueueRef.current.push(payload);
  }, [url]);

  return { sendJsonMessage };
}

function useAudioPlayer() {
  const audioPlayer = useRef<Player>();

  const reset = () => {
    audioPlayer.current = new Player();
    audioPlayer.current.init(SAMPLE_RATE);
  };

  const play = (base64Audio: string) => {
    const binary = atob(base64Audio);
    const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
    const pcmData = new Int16Array(bytes.buffer);
    audioPlayer.current?.play(pcmData);
  };

  const stop = () => {
    audioPlayer.current?.clear();
  };

  return { reset, play, stop };
}

function useRealTime({
  token,
  chatSessionId,
  userId,
  userEmail,
  enableInputAudioTranscription,
  onWebSocketOpen,
  onWebSocketClose,
  onWebSocketError,
  onWebSocketMessage,
  onReceivedChatSession,
  onReceivedResponseDone,
  onReceivedConversationItemCreate,
  onReceivedResponseAudioDelta,
  onReceivedResponseAudioTranscriptDelta,
  onReceivedInputAudioBufferSpeechStarted,
  onReceivedExtensionMiddleTierToolResponse,
  onReceivedInputAudioTranscriptionCompleted,
  onReceivedInputAudioTranscriptionDelta,
  onReceivedError
}: UseRealTimeParameters) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL ?? "";
  const wsBaseUrl = apiBaseUrl.replace(/^http/, "ws");
  const realtimeToken = token?.trim() ?? "";
  const realtimeUser = userEmail?.trim() ?? "";
  const wsEndpoint = wsBaseUrl && realtimeToken && realtimeUser
    ? `${wsBaseUrl}ws/realtime/?token=${encodeURIComponent(realtimeToken)}&user=${encodeURIComponent(realtimeUser)}`
    : "";

  const { sendJsonMessage: originalSendJsonMessage } = useRealtimeWebSocket(wsEndpoint, {
    onOpen: () => onWebSocketOpen?.(),
    onClose: () => onWebSocketClose?.(),
    onError: event => onWebSocketError?.(event),
    onMessage: event => onMessageReceived(event),
    shouldReconnect: () => true
  });

  const sendJsonMessage = (message: any) => {
    originalSendJsonMessage({ ...message });
  };

  const startSession = () => {
    const command: SessionUpdateCommand = {
      type: "session.update",
      session: {
        type: "realtime",
        audio: {
          input: {
            turn_detection: {
              type: "server_vad"
            }
          }
        }
      }
    };

    if (enableInputAudioTranscription) {
      command.session.audio!.input!.transcription = {
        model: "whisper-1",
        language: "en",
        prompt: "Transcribe only English speech. Ignore background noise and non-English languages."
      } as any;
    }

    sendJsonMessage(command);

    const systemMessage = {
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "system",
        content: [
          {
            type: "input_text",
            text: "Marshall Goldsmith Development and leadership coach"
          }
        ]
      }
    };
    sendJsonMessage(systemMessage);
  };

  const addUserAudio = (base64Audio: string) => {
    const command: InputAudioBufferAppendCommand = {
      type: "input_audio_buffer.append",
      audio: base64Audio
    };

    sendJsonMessage(command);
  };

  const inputAudioBufferClear = () => {
    const command: InputAudioBufferClearCommand = {
      type: "input_audio_buffer.clear"
    };

    sendJsonMessage(command);
  };

  const cancelResponse = () => {
    sendJsonMessage({ type: "response.cancel" });
  };

  const sendUserTextQuestion = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) {
      return;
    }

    const userMessage: ConversationItemCreateCommand = {
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "user",
        content: [
          {
            type: "input_text",
            text: trimmed
          }
        ]
      }
    };
    sendJsonMessage(userMessage);

    const responseCommand: ResponseCreateCommand = {
      type: "response.create",
      response: {
        output_modalities: ["audio"]
      }
    };
    sendJsonMessage(responseCommand);
  };

  const onMessageReceived = (event: MessageEvent<any>) => {
    onWebSocketMessage?.(event);

    let message: Message;
    try {
      message = JSON.parse(event.data);
    } catch (e) {
      console.error("Failed to parse JSON message:", e);
      throw e;
    }

    if (typeof message.type === "string") {
      const typeValue = message.type;
      const isDeltaEvent =
        typeValue.includes("input_audio_transcription.delta") ||
        typeValue.includes("input_audio_transcript.delta") ||
        typeValue === "conversation.item.audio_transcription.delta";
      const isCompletedEvent =
        typeValue.includes("input_audio_transcription.completed") ||
        typeValue.includes("input_audio_transcript.completed") ||
        typeValue === "conversation.item.audio_transcription.completed";

      if (isDeltaEvent) {
        onReceivedInputAudioTranscriptionDelta?.(message as ResponseInputAudioTranscriptionDelta);
        return;
      }
      if (isCompletedEvent) {
        onReceivedInputAudioTranscriptionCompleted?.(message as ResponseInputAudioTranscriptionCompleted);
        return;
      }
    }

    switch (message.type) {
      case "chat_session":
        onReceivedChatSession?.(message as ChatSessionMessage);
        break;
      case "response.done":
        onReceivedResponseDone?.(message as ResponseDone);
        break;
      case "response.audio.delta":
      case "response.output_audio.delta":
        onReceivedResponseAudioDelta?.(message as ResponseAudioDelta);
        break;
      case "response.audio_transcript.delta":
      case "response.output_audio_transcript.delta":
        onReceivedResponseAudioTranscriptDelta?.(message as ResponseAudioTranscriptDelta);
        break;
      case "input_audio_buffer.speech_started":
        onReceivedInputAudioBufferSpeechStarted?.(message);
        break;
      case "conversation.item.create":
      case "conversation.item.added":
        onReceivedConversationItemCreate?.(message as ConversationItemCreateEvent);
        break;
      case "extension.middle_tier_tool_response":
        onReceivedExtensionMiddleTierToolResponse?.(message as ExtensionMiddleTierToolResponse);
        break;
      case "error":
        onReceivedError?.(message);
        break;
    }
  };

  return { startSession, addUserAudio, inputAudioBufferClear, cancelResponse, sendUserTextQuestion };
}

// Main Component
interface AudioCallComponentProps {
  onClose?: (summary?: string) => void;
  chatSessionId?: string | null;
}

const AudioCallComponent: React.FC<AudioCallComponentProps> = ({ onClose, chatSessionId: propChatSessionId }) => {
  // State
  const [wsToken, setWsToken] = useState<string | null>(null);
  const [chatSessionId, setChatSessionId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [duration, setDuration] = useState({ minutes: 0, seconds: 0 });
  const [userSpeaking, setUserSpeaking] = useState(false);
  const [marshallSpeaking, setMarshallSpeaking] = useState(true);
  const [isRecording, setIsRecording] = useState(false);

  // Refs
  const isUserSpeakingRef = useRef(false);
  const transcriptRef = useRef("");
  const userQuestionRef = useRef("");
  const sessionInitializedRef = useRef(false);
  const startSessionRef = useRef<(() => void) | null>(null);
  const pendingUserMessageIndexRef = useRef<number | null>(null);
  const marshallResponseRef = useRef("");

  // Store chat message to backend (use same approach as RealtimeVoiceBot.tsx)
  const storeChatMessage = async (sender: "user" | "assistant", message: string) => {
    const trimmedMessage = (message ?? "").trim();
    if (!trimmedMessage) {
      return;
    }

    // Only use valid session IDs (UUID format from chatSessionId or localStorage)
    const sessionIdentifier = chatSessionId?.trim() || localStorage.getItem("chat_session_id");

    if (!sessionIdentifier) {
      console.error("Cannot save message: no session ID available");
      return;
    }

    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}api/v1/chat/messages/store/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          session_id: sessionIdentifier,
          sender,
          message: trimmedMessage,
          refine_question: trimmedMessage
        })
      });
    } catch (error) {
      console.error("Failed to store chat message:", error);
    }
  };

  // Duration timer
  useEffect(() => {
    const timer = setInterval(() => {
      setDuration(prev => {
        if (prev.seconds === 59) {
          return { minutes: prev.minutes + 1, seconds: 0 };
        }
        return { ...prev, seconds: prev.seconds + 1 };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Load WebSocket token and user info
  useEffect(() => {
    const token = localStorage.getItem("wsToken");
    setWsToken(token);

    // Use prop if provided and not empty, otherwise get from localStorage
    if (propChatSessionId?.trim()) {
      setChatSessionId(propChatSessionId);
    } else {
      const sessionId = localStorage.getItem("chat_session_id");
      if (sessionId?.trim()) {
        setChatSessionId(sessionId);
      }
    }
    const storedUserId = localStorage.getItem("user_id");
    setUserId(storedUserId);
    const authenticatedUserString = localStorage.getItem("authenticatedUser");
    if (authenticatedUserString) {
      try {
        const authenticatedUser = JSON.parse(authenticatedUserString);
        if (authenticatedUser && authenticatedUser.email) {
          setUserEmail(authenticatedUser.email);
        } else {
          setUserEmail(authenticatedUserString);
        }
      } catch (e) {
        setUserEmail(authenticatedUserString);
      }
    }
  }, [propChatSessionId]);

  const realtimeReady = Boolean(wsToken?.trim() && userEmail?.trim());

  // Audio player and recorder
  const { reset: resetAudioPlayer, play: playAudio, stop: stopAudioPlayer } = useAudioPlayer();
  const audioPlayerReadyRef = useRef(false);
  const isReceivingAudioRef = useRef(false);
  const audioStopTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const ensureAudioPlayerReady = () => {
    if (audioPlayerReadyRef.current) {
      return;
    }
    resetAudioPlayer();
    audioPlayerReadyRef.current = true;
  };

  const { start: startAudioRecording, stop: stopAudioRecording } = useAudioRecorder({
    onAudioRecorded: (base64) => {
      addUserAudio(base64);
    }
  });

  // Realtime hook
  const { startSession, addUserAudio, inputAudioBufferClear, cancelResponse } = useRealTime({
    token: wsToken,
    chatSessionId: chatSessionId,
    userId: userId,
    userEmail: userEmail,
    enableInputAudioTranscription: true,
    onWebSocketOpen: () => {
      if (!sessionInitializedRef.current) {
        startSessionRef.current?.();
        sessionInitializedRef.current = true;
      }
    },
    onWebSocketClose: () => {
      sessionInitializedRef.current = false;
    },
    onWebSocketError: event => console.error("WebSocket error:", event),
    onReceivedResponseAudioDelta: message => {
      ensureAudioPlayerReady();

      // Set marshallSpeaking = true and keep it true until user interrupts
      // Audio deltas stop arriving before playback ends, so we can't rely on delta timing
      setMarshallSpeaking(true);

      playAudio(message.delta);
    },
    onReceivedInputAudioBufferSpeechStarted: () => {
      // Cancel Marshall's response when user interrupts
      if (marshallSpeaking) {
        cancelResponse();
      }
      // Don't clear the input buffer here: it already holds the start of the
      // user's utterance, and clearing it truncates short phrases like "Hi Marshall"
      stopAudioPlayer();
      isUserSpeakingRef.current = true;
      setUserSpeaking(true);
      setMarshallSpeaking(false);
      transcriptRef.current = "";
      userQuestionRef.current = "";
      marshallResponseRef.current = "";
    },
    onReceivedInputAudioTranscriptionCompleted: message => {
      const finalTranscript = message.transcript?.trim() || transcriptRef.current.trim();
      if (finalTranscript) {
        userQuestionRef.current = finalTranscript;
        transcriptRef.current = "";
        isUserSpeakingRef.current = false;
        setUserSpeaking(false);
        setMarshallSpeaking(true);

        // Save user message immediately
        storeChatMessage("user", finalTranscript);
      }
    },
    onReceivedInputAudioTranscriptionDelta: message => {
      const partialTranscript = message.delta != null
        ? `${transcriptRef.current}${message.delta}`
        : message.transcript;
      if (partialTranscript && typeof partialTranscript === 'string') {
        transcriptRef.current = partialTranscript;
      }
    },
    onReceivedResponseAudioTranscriptDelta: message => {
      if (!isUserSpeakingRef.current) {
        // AI is speaking - collect Marshall's response
        const delta = message.delta || "";
        transcriptRef.current = (transcriptRef.current || "") + delta;
        marshallResponseRef.current = (marshallResponseRef.current || "") + delta;
      }
    },
    onReceivedResponseDone: message => {
      // Response is done, save Marshall's response immediately
      const marshallResponse = marshallResponseRef.current.trim();
      if (marshallResponse) {
        storeChatMessage("assistant", marshallResponse);
        marshallResponseRef.current = ""; // Clear after saving
      }
    },
    onReceivedChatSession: message => {
      // Only create new session if we don't already have a chat session ID
      // If opening audio from existing chat (via prop), use that chat's session
      if (!propChatSessionId?.trim()) {
        // No existing chat, so create new session
        setChatSessionId(message.session_id);
        localStorage.setItem("chat_session_id", message.session_id);
      }
    },
    onReceivedError: message => {
      // Ignore harmless "no active response" cancellation errors
      if ((message as any).error?.code === 'response_cancel_not_active') {
        return;
      }
      console.error("OpenAI Realtime error:", message);
    }
  });

  startSessionRef.current = startSession;

  const ensureSessionInitialized = () => {
    if (!realtimeReady) {
      console.warn("Realtime session is not ready.");
      return false;
    }
    if (sessionInitializedRef.current) {
      return true;
    }
    startSession();
    sessionInitializedRef.current = true;
    return true;
  };

  // Start recording on mount
  useEffect(() => {
    const startRecordingOnMount = async () => {
      try {
        if (!ensureSessionInitialized()) {
          return;
        }
        await startAudioRecording();
        resetAudioPlayer();
        audioPlayerReadyRef.current = true;
        setIsRecording(true);
        setIsMuted(false);
      } catch (error) {
        console.error("Failed to initialize audio recording:", error);
      }
    };

    if (!realtimeReady) {
      return;
    }

    startRecordingOnMount();
  }, [realtimeReady]);


  const handleMicToggle = async () => {
    if (!isRecording) {
      return;
    }

    if (isMuted) {
      await startAudioRecording();
      setIsMuted(false);
    } else {
      await stopAudioRecording();
      inputAudioBufferClear();
      setIsMuted(true);
      setUserSpeaking(false);
      isUserSpeakingRef.current = false;
    }
  };

  const handleClose = async () => {
    // Stop any ongoing response immediately
    if (marshallSpeaking) {
      cancelResponse();
    }
    stopAudioPlayer();
    inputAudioBufferClear();

    // Messages are already saved as they arrive, just clean up and close
    if (isRecording) {
      await stopAudioRecording();
      setIsRecording(false);
      setIsMuted(false);
      setUserSpeaking(false);
      setMarshallSpeaking(false);
      isUserSpeakingRef.current = false;
      audioPlayerReadyRef.current = false;
      sessionInitializedRef.current = false;
      isReceivingAudioRef.current = false;

      // Clear any pending stop timeout
      if (audioStopTimeoutRef.current) {
        clearTimeout(audioStopTimeoutRef.current);
        audioStopTimeoutRef.current = null;
      }

      // Reset refs
      userQuestionRef.current = "";
      marshallResponseRef.current = "";
      transcriptRef.current = "";
    }

    // Generate and fetch summary
    try {
      const sessionId = chatSessionId || localStorage.getItem("chat_session_id");
      
      if (sessionId) {
        const token = localStorage.getItem("authenticationToken");

        // First, generate the summary
        const generateResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}api/v1/chat/${sessionId}/summary/generate/`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          }
        });

        // Then fetch the generated summary
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}api/v1/chat/${sessionId}/summary/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          }
        });
        
        const data = await response.json();
        onClose?.(data?.summary);
      } else {
        console.log("No sessionId found");
        onClose?.();
      }
    } catch (error) {
      console.error("Error with summary:", error);
      onClose?.();
    }
  };

  return (
    <div className="audio-call-component">
      {/* Status badge */}
      <div className="audio-call-status-badge">
        <span className="status-dot"></span>
        <span className="status-text">{marshallSpeaking ? "Marshall is speaking" : "Marshall is listening"}</span>
        <span className="duration-text">
          · {String(duration.minutes).padStart(2, '0')}:{String(duration.seconds).padStart(2, '0')}
        </span>
      </div>

      {/* Avatar section */}
      <div className="audio-call-avatar-section">
        <div className="avatar-container">
          <img
            src={marshallImage}
            alt="Marshall"
            className="avatar-image"
          />
        </div>
      </div>

      {/* Controls section */}
      <div className="audio-call-controls-section">
        {/* Left visualization - User's audio */}
        <div className={`audio-visualization ${userSpeaking && !isMuted ? "" : "muted-bars"}`}>
          <div className={`wave-bar ${userSpeaking && !isMuted ? "" : "muted"}`}></div>
          <div className={`wave-bar ${userSpeaking && !isMuted ? "" : "muted"}`}></div>
          <div className={`wave-bar ${userSpeaking && !isMuted ? "" : "muted"}`}></div>
          <div className={`wave-bar ${userSpeaking && !isMuted ? "" : "muted"}`}></div>
          <div className={`wave-bar ${userSpeaking && !isMuted ? "" : "muted"}`}></div>
          <div className={`wave-bar ${userSpeaking && !isMuted ? "" : "muted"}`}></div>
          <div className={`wave-bar ${userSpeaking && !isMuted ? "" : "muted"}`}></div>
          <div className={`wave-bar ${userSpeaking && !isMuted ? "" : "muted"}`}></div>
          <div className={`wave-bar ${userSpeaking && !isMuted ? "" : "muted"}`}></div>
          <div className={`wave-bar ${userSpeaking && !isMuted ? "" : "muted"}`}></div>
          <div className={`wave-bar ${userSpeaking && !isMuted ? "" : "muted"}`}></div>
        </div>

        {/* Mute button */}
        <button
          className={`audio-control-btn mute-btn ${isMuted ? 'muted' : ''}`}
          onClick={handleMicToggle}
          title={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted ? (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_176_5729)">
            <path d="M1.66699 1.6665L18.3337 18.3332" stroke="#FBF8F2" strokeWidth="1.41667" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M7.50025 7.49979V4.16646C7.49266 3.62642 7.66017 3.09844 7.97771 2.66154C8.29524 2.22465 8.74572 1.90234 9.26173 1.74284C9.77775 1.58335 10.3315 1.59525 10.8402 1.77677C11.3489 1.95829 11.7851 2.29966 12.0836 2.74979" stroke="#FBF8F2" strokeWidth="1.41667" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12.4997 9.5835V10.8335C12.504 11.2911 12.3826 11.741 12.1488 12.1344C11.9151 12.5277 11.5778 12.8493 11.1738 13.0642C10.7698 13.2791 10.3146 13.379 9.85779 13.353C9.40095 13.327 8.96001 13.1761 8.58301 12.9168" stroke="#FBF8F2" strokeWidth="1.41667" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M4.16622 8.3335C4.15757 9.37802 4.42956 10.4057 4.95377 11.3092C5.47798 12.2127 6.23517 12.9589 7.14627 13.4698C8.05736 13.9807 9.08893 14.2375 10.1332 14.2136C11.1775 14.1896 12.1962 13.8857 13.0829 13.3335" stroke="#FBF8F2" strokeWidth="1.41667" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M10 14.1665V17.4998" stroke="#FBF8F2" strokeWidth="1.41667" strokeLinecap="round" strokeLinejoin="round"/>
            </g>
            <defs>
            <clipPath id="clip0_176_5729">
            <rect width="20" height="20" fill="white"/>
            </clipPath>
            </defs>
          </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.5 4.1665C12.5 2.78579 11.3807 1.6665 10 1.6665C8.61929 1.6665 7.5 2.78579 7.5 4.1665V9.1665C7.5 10.5472 8.61929 11.6665 10 11.6665C11.3807 11.6665 12.5 10.5472 12.5 9.1665V4.1665Z" stroke="#22201B" strokeWidth="1.41667" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M4.16699 8.3335C4.16699 9.88059 4.78157 11.3643 5.87554 12.4583C6.9695 13.5522 8.45323 14.1668 10.0003 14.1668C11.5474 14.1668 13.0312 13.5522 14.1251 12.4583C15.2191 11.3643 15.8337 9.88059 15.8337 8.3335" stroke="#22201B" strokeWidth="1.41667" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 14.1665V17.4998" stroke="#22201B" strokeWidth="1.41667" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </button>

        {/* End call button */}
        <button
          className="audio-control-btn end-call-btn"
          onClick={handleClose}
          disabled={!isRecording}
          title="End Call"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2.5 9.1665C6.66667 5.83317 13.3333 5.83317 17.5 9.1665L15.6667 10.9998C15.1667 11.4998 14.3333 11.4165 13.9167 10.8332L13.0833 9.6665C10.5833 8.9165 7.75 8.9165 5.25 9.6665L4.41667 10.8332C4 11.4165 3.16667 11.4998 2.66667 10.9998L2.5 9.1665Z" stroke="#FBF8F2" strokeWidth="1.41667" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className="end-call-text">End session</span>
        </button>

        {/* Right visualization - Marshall's audio */}
        <div className={`audio-visualization ${marshallSpeaking ? "" : "muted-bars"}`}>
          <div className={`wave-bar ${marshallSpeaking ? "" : "muted"}`}></div>
          <div className={`wave-bar ${marshallSpeaking ? "" : "muted"}`}></div>
          <div className={`wave-bar ${marshallSpeaking ? "" : "muted"}`}></div>
          <div className={`wave-bar ${marshallSpeaking ? "" : "muted"}`}></div>
          <div className={`wave-bar ${marshallSpeaking ? "" : "muted"}`}></div>
          <div className={`wave-bar ${marshallSpeaking ? "" : "muted"}`}></div>
          <div className={`wave-bar ${marshallSpeaking ? "" : "muted"}`}></div>
          <div className={`wave-bar ${marshallSpeaking ? "" : "muted"}`}></div>
          <div className={`wave-bar ${marshallSpeaking ? "" : "muted"}`}></div>
          <div className={`wave-bar ${marshallSpeaking ? "" : "muted"}`}></div>
          <div className={`wave-bar ${marshallSpeaking ? "" : "muted"}`}></div>
        </div>
      </div>

      {/* Footer text */}
      <div className="audio-call-footer">
        <p>Voice is AI-generated in the spirit of Marshall's work.</p>
      </div>
    </div>
  );
};

export default AudioCallComponent;
